import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';
import { MIN_LOS_NO_RESTRICTION_VALUE } from 'lumen/utils/constants/pricing-assistant-constants';
import type MinLosRestrictionState from 'lumen/utils/pricing-assistant/min-los-restriction-state';

const AMOUNT_MORE_KEY = tKey('general.amount_more');

const MAX_NAMED_RATE_GROUPS = 2;

export interface RateGroupMinLos {
  hasOverride: boolean;
  isLoading: boolean;
  rateGroupId: string;
  value: number | null;
}

export interface RateGroupMinLosRow extends Omit<
  RateGroupMinLos,
  'rateGroupId'
> {
  rateGroupIds: string[];
}

/**
 * Collapses rate groups that would render an identical MinLOS row into one row.
 *
 * `hasOverride` is part of the merge key because it changes how the value is drawn:
 * two groups both showing "MinLOS 2" must stay apart when only one shows the
 * override indicator — the icon asserts "automation paused" for every group in the
 * row, so OR-ing it like `isLoading` would put a false claim on groups without an
 * override.
 *
 * `isLoading` is not part of the key, so a merged row can cover both a settled group
 * and one mid-push. Such a row loads when any of its groups does, matching the single
 * rate group row it replaces: a skeleton never asserts a value that is about to change.
 *
 * RATEGROUP TODO (COA-4197): input order is preserved, so rows follow the order
 * configured rate groups arrive in; replace with the admin-configured ranking.
 */
export function groupRateGroupsByMinLos(
  entries: RateGroupMinLos[],
): RateGroupMinLosRow[] {
  const rows: RateGroupMinLosRow[] = [];

  for (const entry of entries) {
    const existingRow = rows.find(
      (row) =>
        row.value === entry.value && row.hasOverride === entry.hasOverride,
    );

    if (existingRow) {
      existingRow.rateGroupIds.push(entry.rateGroupId);
      existingRow.isLoading = existingRow.isLoading || entry.isLoading;
      continue;
    }

    rows.push({
      hasOverride: entry.hasOverride,
      isLoading: entry.isLoading,
      rateGroupIds: [entry.rateGroupId],
      value: entry.value,
    });
  }

  return rows;
}

/**
 * Labels a merged row, e.g. "Group 1, Group 2, 2 more...".
 *
 * Names are shown verbatim rather than translated: the hotel wrote them in CEMA.
 */
export function formatRateGroupLabel(
  rateGroupNames: string[],
  intl: Services['intl'],
): string {
  const labelParts = rateGroupNames.slice(0, MAX_NAMED_RATE_GROUPS);
  const remainingCount = rateGroupNames.length - labelParts.length;

  if (remainingCount > 0) {
    labelParts.push(intl.t(AMOUNT_MORE_KEY, { count: remainingCount }));
  }

  return labelParts.join(', ');
}

/**
 * Whether a rate group's MinLOS column shows a restriction: the recommendation when
 * automation is on, the override (or its default seed) when off. "No MinLOS
 * restriction" and "No recommendation" both count as not restricted.
 */
export function isMinLosRestricted(
  state: Pick<
    MinLosRestrictionState,
    'automationOn' | 'restrictionRecommendation' | 'value'
  >,
): boolean {
  const value = state.automationOn
    ? state.restrictionRecommendation?.minLosValue
    : state.value;

  return value != null && value !== MIN_LOS_NO_RESTRICTION_VALUE;
}
